import LoadingIndicator from '@/components/LoadingIndicator'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { ccDashboardCancelOrder } from '@/legacyApi/ccOrder'
import { useLazyQuery } from '@apollo/client'
import { Close, Info } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CCOrderedItemCard as OrderedItemCard } from './ItemCard/OrderedItemCard'
import { CCOrderedItemCardV2 as OrderedItemCardV2 } from './ItemCard/OrderedItemCardV2'
import MenuItemDetailV2 from './ItemDetail/MenuItemDetailV2'
import OrderedItemDetail from './ItemDetail/OrderedItemDetail'

const canteenOrderedItemsMenuDataFragment = graphql(`
  fragment CanteenOrderedItems_MenuDataFragment on CCMealService {
    orderedItems(input: { acrossAllLocations: true }) {
      items {
        id
        ...CanteenOrderedItemCard_OrderItemFragment
        ...CanteenOrderedItemCard_OrderItemFragmentV2
      }
    }
  }
`)

const canteenOrderedItemsItemDataQuery_V2 = graphql(`
  query CanteenOrderedItemDetail_ItemDataQuery_V2($id: ID!) {
    ccOrderItem(id: $id) {
      id
      name
      qty
      canCancel
      description
      dietaries
      allergens
      subAllergens {
        cereals
        nuts
      }
      liveMenuItem {
        _id
        coreLiveItem {
          ...CanteenMenuItemDetail_ItemDataFragmentV2
        }
      }
      vendor {
        ...CanteenMenuItemDetail_VendorDataFragmentV2
      }
      options {
        ...CanteenMenuItemDetail_OrderedOptionsDataFragmentV2
      }
    }
  }
`)

const canteenOrderedItemsItemDataQuery = graphql(`
  query CanteenOrderedItemDetail_ItemDataQuery($id: ID!) {
    ccOrderItem(id: $id) {
      id
      ...CanteenOrderedItemDetail_ItemDataFragment
    }
  }
`)

type Props = {
  menuData: FragmentType<typeof canteenOrderedItemsMenuDataFragment>
  hidePrices: boolean
  onSuccessfulCancellation: () => Promise<void>
  useNewCcUi?: boolean
}

const OrderedItemsGraphql: React.FC<Props> = ({
  menuData: menuDataProp,
  hidePrices,
  onSuccessfulCancellation,
  useNewCcUi,
}) => {
  const menuData = useFragment(canteenOrderedItemsMenuDataFragment, menuDataProp)
  const theme = useTheme()
  const { t } = useTranslation()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  // explicitly check for portrait to handle tablet views
  const isPortrait = useMediaQuery('(orientation: portrait)')

  const [itemsPendingCancellation, setItemsPendingCancellation] = useState<Set<number>>(new Set())
  const [itemToBeCancelled, setItemToBeCancelled] = useState<number | null>(null)
  const [selectedItemId, setSelectedItemId] = useState('')
  const [getItemData, { data: selectedItemData, loading: loadingItem }] = useLazyQuery(
    canteenOrderedItemsItemDataQuery,
  )
  const [getItemDataV2, { data: selectedItemDataV2, loading: loadingItemV2 }] = useLazyQuery(
    canteenOrderedItemsItemDataQuery_V2,
  )

  useEffect(() => {
    if (!menuData.orderedItems.items.length || !itemsPendingCancellation.size) return
    const itemsBeingCancelled = Array.from(itemsPendingCancellation)
    const itemsSuccessfullyCancelled = itemsBeingCancelled.reduce<number[]>(
      (cancelledItems, itemPending) => {
        const itemWasCancelled = !menuData.orderedItems.items.some(
          (item) => item.id === itemPending,
        )
        return [...cancelledItems, ...(itemWasCancelled ? [itemPending] : [])]
      },
      [],
    )

    if (!itemsSuccessfullyCancelled.length) return
    setItemsPendingCancellation((prev) => {
      const newState = new Set(prev)
      itemsSuccessfullyCancelled.forEach((itemPendingCancellation) => {
        newState.delete(itemPendingCancellation)
      })
      return newState
    })
  }, [itemsPendingCancellation, menuData.orderedItems.items])

  const handleSeeMore = useCallback(
    (orderItemId: string) => {
      setSelectedItemId(orderItemId)
      getItemData({ variables: { id: orderItemId } })
      getItemDataV2({ variables: { id: orderItemId } })
    },
    [getItemData, getItemDataV2],
  )

  const handleCloseItemDialog = () => {
    setSelectedItemId('')
  }

  const handleCancelItem = useCallback((itemId: number) => {
    setItemToBeCancelled(itemId)
  }, [])

  const handleDeclineCancellation = () => {
    setItemToBeCancelled(null)
  }

  const handleConfirmCancellation = async (itemId?: number) => {
    if (selectedItemId) setSelectedItemId('')
    const itemIdForCancellation = itemId ?? itemToBeCancelled
    if (itemIdForCancellation) {
      setItemsPendingCancellation((prev) => {
        const newState = new Set(prev)
        newState.add(itemIdForCancellation)
        return newState
      })
      setItemToBeCancelled(null)
      await ccDashboardCancelOrder(itemIdForCancellation)
      onSuccessfulCancellation()
    }
  }

  return menuData.orderedItems.items.length > 0 ? (
    <>
      {useNewCcUi ? (
        <Dialog
          onClose={() => handleDeclineCancellation()}
          open={Boolean(itemToBeCancelled && !selectedItemId)}>
          <DialogTitle variant="h5">{t('cc:confirmCancel')}</DialogTitle>
          <DialogContent dividers={false}>
            <DialogContentText>{t('cc:cancelWarningText')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeclineCancellation}>{t('cc:keepOrderButton')}</Button>
            <Button
              variant="contained"
              onClick={() => {
                handleConfirmCancellation()
              }}>
              {t('cc:cancelOrderButton')}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog
          onClose={() => handleDeclineCancellation()}
          open={Boolean(itemToBeCancelled && !selectedItemId)}>
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Info color="warning" />
              <Typography>Are you sure you want to cancel this item?</Typography>
            </Stack>
          </DialogTitle>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                handleConfirmCancellation()
              }}>
              Cancel it
            </Button>
            <Button onClick={handleDeclineCancellation}>Keep it</Button>
          </DialogActions>
        </Dialog>
      )}
      {useNewCcUi ? (
        <Dialog
          maxWidth={'md'}
          sx={{
            '.MuiDialog-paper': {
              height: '80vh',
              ...(!fullScreen && isPortrait && { maxHeight: '56vh' }),
              minWidth: { md: '75vw', sm: '90vw', xs: '95vw' },
              maxWidth: { xs: '95vw' },
              borderRadius: 5,
            },
          }}
          fullScreen={fullScreen}
          onClose={() => setSelectedItemId('')}
          open={Boolean(selectedItemId)}>
          {loadingItem ? <LoadingIndicator /> : null}
          {selectedItemDataV2 &&
          selectedItemDataV2.ccOrderItem.liveMenuItem.coreLiveItem &&
          selectedItemId &&
          !loadingItemV2 ? (
            <MenuItemDetailV2
              isOrderedItem={true}
              orderedItemId={Number(selectedItemId)}
              canCancel={selectedItemDataV2.ccOrderItem.canCancel}
              onCancelItem={handleConfirmCancellation}
              selectedVariationId={selectedItemDataV2.ccOrderItem.liveMenuItem._id}
              liveVariationQuantityData={{ orderLimit: null, quantityRemaining: null }}
              itemData={selectedItemDataV2.ccOrderItem.liveMenuItem.coreLiveItem}
              orderedOptionsData={selectedItemDataV2.ccOrderItem.options}
              vendorData={selectedItemDataV2.ccOrderItem.vendor}
              hidePrices={hidePrices}
              onClose={() => setSelectedItemId('')}
              fullScreen={fullScreen}
            />
          ) : null}
        </Dialog>
      ) : (
        <Dialog
          sx={{
            '.MuiPaper-root': {
              height: { md: '93vh', xs: '100%' },
              minHeight: { md: '93vh', xs: '100%' },
              overflow: 'scroll',
            },
          }}
          fullScreen={fullScreen}
          onClose={handleCloseItemDialog}
          maxWidth="md"
          fullWidth
          open={Boolean(selectedItemId)}>
          <IconButton onClick={handleCloseItemDialog} sx={{ position: 'absolute', right: 0 }}>
            <Close />
          </IconButton>
          {loadingItem ? <LoadingIndicator /> : null}
          {selectedItemData && !loadingItem ? (
            <OrderedItemDetail
              cancelItem={(itemId) => {
                handleConfirmCancellation(itemId)
              }}
              itemData={selectedItemData.ccOrderItem}
            />
          ) : null}
        </Dialog>
      )}
      <Grid spacing={1} container alignItems="stretch">
        <Grid item xs={12}>
          <Typography variant="subtitle2">{t('cc:yourOrder')}</Typography>
        </Grid>
        {menuData.orderedItems.items.map((item) => (
          <Grid md={6} lg={6} xl={4} xs={12} key={item.id} item>
            {useNewCcUi ? (
              <OrderedItemCardV2
                showItemDetails={handleSeeMore}
                cancelOrder={handleCancelItem}
                orderItem={item}
                hidePrices={hidePrices}
                pendingCancellation={itemsPendingCancellation.has(item.id)}
              />
            ) : (
              <OrderedItemCard
                pendingCancellation={itemsPendingCancellation.has(item.id)}
                showItemDetails={handleSeeMore}
                cancelOrder={handleCancelItem}
                orderItem={item}
                hidePrices={hidePrices}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </>
  ) : null
}

export default OrderedItemsGraphql
